/**
 * Administration › Modules par filiale (groupe AGORA-GRP-IT-ADMIN) : pour chaque filiale, activer / désactiver les
 * métiers, modules, sous-modules et le pilotage. Un nœud est actif s'il l'est lui-même ET tous ses ancêtres ; un nœud
 * désactivé disparaît des menus, du catalogue (API et MCP) et son API refuse les appels (`module_inactif`).
 * API : GET /v1/admin/activations, PUT /v1/admin/activations/{filiale} (enregistrement immédiat, audit côté serveur).
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CATALOGUE, FILIALES, OUTILS, PILOTAGE, noeud as noeudPartage, type Activations, type MetierId, cleLibre } from '@agora/shared';
import { Button, ChoiceList, Icon, ModuleTag, RecordSkeleton, ShapeFlag, StatusBadge, Toggle, cx } from '../../design';
import { ErreurApi, useApi, useFiliale, useMoi, useT } from '../../socle';
import { NonAutorise } from './PageNoeud';

const CLE = ['admin', 'activations'] as const;

export function AdministrationModules() {
  const { t, lib } = useT();
  const moi = useMoi();
  const api = useApi();
  const qc = useQueryClient();
  const { filiale: filialeCourante } = useFiliale();
  const [filiale, setFiliale] = React.useState(() => (FILIALES.some((f) => f.code === filialeCourante) ? filialeCourante : FILIALES[0].code));
  const [message, setMessage] = React.useState<{ type: 'erreur' | 'succes'; texte: string } | null>(null);

  const requete = useQuery({ queryKey: CLE, queryFn: () => api.get<Activations>('/v1/admin/activations'), enabled: moi.admin });

  const enregistrer = useMutation({
    mutationFn: (v: { filiale: string; changements: Record<string, boolean> }) =>
      api.put<Activations>(`/v1/admin/activations/${encodeURIComponent(v.filiale)}`, v.changements),
    // Mise à jour optimiste : l'interrupteur bascule tout de suite, retour arrière en cas d'erreur.
    onMutate: async (v) => {
      setMessage(null);
      await qc.cancelQueries({ queryKey: CLE });
      const avant = qc.getQueryData<Activations>(CLE);
      if (avant) {
        qc.setQueryData<Activations>(CLE, { ...avant, filiales: { ...avant.filiales, [v.filiale]: { ...avant.filiales[v.filiale], ...v.changements } } });
      }
      return { avant };
    },
    onError: (e, _v, contexte) => {
      if (contexte?.avant) qc.setQueryData(CLE, contexte.avant);
      setMessage({ type: 'erreur', texte: t(`erreurs.${e instanceof ErreurApi ? e.code : 'interne'}`) });
    },
    onSuccess: (resultat) => {
      qc.setQueryData(CLE, resultat);
      setMessage({ type: 'succes', texte: t('commun.enregistre') });
      // Menus, pages et accueil reflètent immédiatement la nouvelle activation.
      void qc.invalidateQueries({ queryKey: ['catalogue'] });
      void qc.invalidateQueries({ queryKey: ['accueil'] });
    },
  });

  if (!moi.admin) return <NonAutorise titre={t('admin.modules.titre')} />;

  const donnees = requete.data;
  const valeurs = donnees?.filiales[filiale] || {};
  const propre = (id: string) => valeurs[id] ?? donnees?.parDefaut ?? true;
  /** Premier ancêtre désactivé (du plus proche au plus lointain), qui rend le nœud inactif. */
  const bloquant = (id: string) => noeudPartage(id)?.parents.find((p) => !propre(p));
  const effectif = (id: string) => propre(id) && !bloquant(id);
  const basculer = (changements: Record<string, boolean>) => enregistrer.mutate({ filiale, changements });

  const ligne = (id: string, niveau: 1 | 2 | 3) => {
    const parent = bloquant(id);
    const aide = parent ? t('admin.modules.desactivePar', { parent: lib(parent) }) : effectif(id) ? t('admin.modules.actif') : t('admin.modules.inactif');
    return (
      <div key={id} className={cx('agx-admin-noeud', `agx-niveau-${niveau}`, parent && 'agx-grise')}>
        <Toggle label={lib(id)} help={aide} checked={propre(id)} onChange={(v) => basculer({ [id]: v })} />
      </div>
    );
  };

  const total = CATALOGUE.flatMap((m) => [m.id, ...m.modules.flatMap((mo) => [mo.id, ...mo.sousModules.map((s) => s.id)])]).concat(PILOTAGE.map((p) => p.id), OUTILS.map((o) => o.id));
  const actifs = total.filter(effectif).length;

  return (
    <>
      <div className="ag-pagehead">
        <div>
          <h1>{t('admin.modules.titre')}</h1>
          <p className="agx-sous-titre">{t('admin.modules.sousTitre')}</p>
        </div>
      </div>

      <section className="ag-glass-card agx-carte" aria-label={t('admin.modules.filiale')}>
        <h2 className="agx-titre-carte">{t('admin.modules.filiale')}</h2>
        {/* Choix de la filiale : liste de choix du design (Radix RadioGroup : flèches, Début / Fin, un seul arrêt de tabulation). */}
        <ChoiceList
          className="agx-filiales-choix" ariaLabel={t('admin.modules.filiale')} value={filiale}
          options={FILIALES.map((f) => ({
            value: f.code, label: t(cleLibre('pays', f.pays)),
            leading: <ShapeFlag country={f.pays} size={20} />, trailing: <span className="ag-mono agx-code">{f.code}</span>,
          }))}
          onChange={(code) => { setFiliale(code); setMessage(null); }}
        />
        <div className="agx-ligne-badges">
          {donnees ? <StatusBadge status="info" label={t('admin.modules.compte', { actifs, total: total.length })} /> : null}
          {donnees ? <StatusBadge status="neutral" label={t('admin.modules.parDefaut', { valeur: donnees.parDefaut ? t('admin.modules.actif') : t('admin.modules.inactif') })} /> : null}
        </div>
        {message ? (
          <div className={cx('agx-message', message.type === 'succes' && 'agx-message-succes')} role={message.type === 'erreur' ? 'alert' : 'status'}>
            <Icon name={message.type === 'erreur' ? 'bell' : 'check'} size={16} /><span>{message.texte}</span>
          </div>
        ) : null}
      </section>

      {/* Chargement : squelette de la grille des métiers (une carte d'interrupteurs par métier). */}
      {requete.isLoading ? <RecordSkeleton label={t('app.chargement')} header={false} cards={3} fields={6} /> : null}
      {requete.error ? (
        <div className="agx-message" role="alert"><Icon name="bell" size={16} /><span>{t(`erreurs.${requete.error instanceof ErreurApi ? requete.error.code : 'interne'}`)}</span></div>
      ) : null}

      {donnees ? (
        <div className="agx-grille">
          {CATALOGUE.map((m) => {
            const ids = [m.id, ...m.modules.flatMap((mo) => [mo.id, ...mo.sousModules.map((s) => s.id)])];
            return (
              <section key={m.id} className="ag-glass-card agx-carte" aria-label={lib(m.id)}>
                <div className="agx-admin-tete">
                  <ModuleTag metier={m.id as MetierId} label={lib(m.id)} />
                  <div className="agx-actions">
                    <Button size="sm" variant="quiet" icon="check" onClick={() => basculer(Object.fromEntries(ids.map((id) => [id, true])))}>{t('admin.modules.toutActiver')}</Button>
                    <Button size="sm" variant="quiet" icon="minus" onClick={() => basculer(Object.fromEntries(ids.map((id) => [id, false])))}>{t('admin.modules.toutDesactiver')}</Button>
                  </div>
                </div>
                <div className="agx-liste">
                  {ligne(m.id, 1)}
                  {m.modules.flatMap((mo) => [ligne(mo.id, 2), ...mo.sousModules.map((s) => ligne(s.id, 3))])}
                </div>
              </section>
            );
          })}
          <section className="ag-glass-card agx-carte" aria-label={t('nav.pilotage')}>
            <h2 className="agx-titre-carte">{t('nav.pilotage')}</h2>
            <div className="agx-liste">{PILOTAGE.map((p) => ligne(p.id, 1))}</div>
          </section>
          <section className="ag-glass-card agx-carte" aria-label={t('nav.outils')}>
            <h2 className="agx-titre-carte">{t('nav.outils')}</h2>
            <div className="agx-liste">{OUTILS.map((o) => ligne(o.id, 1))}</div>
          </section>
        </div>
      ) : null}
    </>
  );
}
